import { z } from 'zod';
import { TOKEN_IDS } from '../constants/tokens.js';
import { TOKEN_DECIMALS } from '../constants/precision.js';

/**
 * Decimal string schema.
 *
 * Accepts a string that looks like a signed decimal number with at most
 * `TOKEN_DECIMALS` fractional digits. Rejects scientific notation, trailing
 * dots, and multiple signs. This runs at every API boundary before the value
 * reaches the LedgerService, so the service itself never has to parse user
 * input.
 *
 * Why string instead of number: JSON `number` loses precision >2^53 and this
 * system routinely handles 18-decimal token amounts. We keep them as strings
 * end-to-end and only convert to `Decimal` in the service layer.
 */
const decimalStringRegex = new RegExp(`^-?\\d+(\\.\\d{1,${TOKEN_DECIMALS}})?$`);
export const DecimalString = z
  .string()
  .regex(decimalStringRegex, 'must be a decimal with up to 18 fractional digits');

/** Positive decimal (> 0). */
export const PositiveDecimalString = DecimalString.refine((v) => !v.startsWith('-') && v !== '0', {
  message: 'must be > 0',
});

/** Admin InputNumber often posts JSON numbers — coerce to decimal string. */
export const CoercedPositiveDecimalString = z.union([
  z
    .number()
    .finite()
    .gt(0)
    .transform((n) => String(n)),
  PositiveDecimalString,
]);

/** Non-negative decimal (>= 0). */
export const NonNegativeDecimalString = DecimalString.refine((v) => !v.startsWith('-'), {
  message: 'must be >= 0',
});

/** Token identifier — narrows to the TokenId union. */
export const TokenIdSchema = z.enum(TOKEN_IDS);

/** Standard pagination query. */
export const PaginationQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(200).default(20),
  sort: z.string().optional(),
  order: z.enum(['asc', 'desc']).optional().default('desc'),
});

/** ISO 8601 date string. */
export const IsoDateString = z.string().datetime({ offset: true });

/**
 * Idempotency key header value. Accepts a UUID or any opaque string between
 * 8 and 64 chars. The backend stores this on the ledger entry to provide
 * at-most-once semantics on POSTs that trigger balance mutations.
 */
export const IdempotencyKey = z
  .string()
  .min(8)
  .max(64)
  .regex(/^[a-zA-Z0-9_-]+$/);
